import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaSearch, 
  FaBook, 
  FaNewspaper, 
  FaStar, 
  FaPlus, 
  FaExternalLinkAlt,
  FaCode,
  FaChartLine,
  FaGlobe,
  FaDatabase,
  FaRobot,
  FaFileAlt,
  FaMedal,
  FaUniversity,
  FaUsers,
  FaChalkboardTeacher,
  FaCogs
} from 'react-icons/fa';
import './ResearchPublications.css';

// Sample data - replace with actual data from your portfolioData when available
const researchData = [
  {
    id: 1,
    title: "Maintaining the structural integrity of pipeline in the energy sector: A critical review as a holistic measure for safety management with AI",
    authors: "Uchenna Anyaoha, et al.",
    venue: "International Journal of Pressure Vessels and Piping",
    year: "2024",
    abstract: "Comprehensive review of artificial intelligence applications for pipeline safety and structural integrity management in the energy sector, with emphasis on holistic safety measures.",
    paperUrl: "#",
    codeUrl: null,
    type: "journal",
    category: "energy",
    citation: 24,
    importance: 95
  },
  {
    id: 2,
    title: "Concrete performance prediction using boosting smooth transition regression trees (BooST)",
    authors: "Uchenna Anyaoha,  Xiang Peng, Zheng Liu",
    venue: "SPIE Smart Structures + Nondestructive Evaluation",
    year: "2019",
    abstract: "Advanced machine learning approach for predicting concrete performance using boosting smooth transition regression trees, addressing accuracy challenges in concrete strength prediction.",
    paperUrl: "https://www.spiedigitallibrary.org/conference-proceedings-of-spie/10971/109710I/Concrete-performance-prediction-using-boosting-smooth-transition-regression-trees-BooST/10.1117/12.2518279.short",
    codeUrl: null,
    type: "conference",
    category: "engineering",
    citation: 18,
    importance: 92
  },
  {
    id: 3,
    title: "Analysis of Magnetic-Flux Leakage (MFL) Data for Pipeline Corrosion Assessment",
    authors: "Xiang Peng, Uchenna Anyaoha, Zheng Liu, Kazuhiko Tsukada",
    venue: "IEEE Transactions on Magnetics",
    year: "2020",
    abstract: "Advanced research on using magnetic flux leakage analysis techniques to detect and assess corrosion in oil and gas pipelines, contributing to improved pipeline integrity management and defect detection.",
    paperUrl: "https://ieeexplore.ieee.org/abstract/document/9039686",
    codeUrl: null,
    type: "journal",
    category: "energy",
    citation: 142,
    importance: 90
  },
  
  {
    id: 4,
    title: "Applied computational analyses for concrete compressive strength performance assessment",
    authors: "Uchenna Anyaoha",
    venue: "The University of British Columbia",
    year: "2019",
    abstract: "Novel machine learning model, Boosting Smooth Transition Regression Trees (BooST), developed and proposed by the author for predicting concrete strength, demonstrating significant improvements in structural integrity assessment and quality control.",
    paperUrl: "https://open.library.ubc.ca/soa/cIRcle/collections/ubctheses/24/items/1.0380852",
    codeUrl: null,
    type: "thesis",
    category: "engineering",
    citation: 17,
    importance: 98
  },
  {
    id: 6,
    title: "Soft computing in estimating the compressive strength for high-performance concrete via concrete composition appraisal",
    authors: "Uchenna Anyaoha, Amirhossein Zaji, Zheng Liu",
    venue: "ScienceDirect - Construction and Building Materials Journal",
    year: "2020",
    abstract: "Machine learning approach for detecting anomalies in fused deposition modeling (FDM) 3D printing processes to improve quality control and manufacturing efficiency.",
    paperUrl: "https://www.sciencedirect.com/science/article/abs/pii/S095006182031477X",
    codeUrl: null,
    type: "journal",
    category: "engineering",
    citation: 39,
    importance: 91
  },
  {
    id: 7,
    title: "Parameterizing Magnetic Flux Leakage Data for Pipeline Corrosion Defect Retrieval",
    authors: "Xiang Peng, Chengkai Zhang, Uchenna Anyaoha, Kevin Siggers, Zheng Liu",
    venue: "IEEE International Symposium on Industrial Electronics (ISIE) ",
    year: "2019",
    abstract: "Development of integrated sensing technologies for parameterizing and metricizing magnetic flux leakage data for pipeline corrosion monitoring, combining multiple data sources for comprehensive structural health monitoring of critical infrastructure.",
    paperUrl: "https://ieeexplore.ieee.org/abstract/document/8781224",
    codeUrl: null,
    type: "conference",
    category: "energy",
    citation: 8,
    importance: 89
  },
  {
    id: 8,
    title: "Contributions of the Weakness of Local Aggregates to the Failure of Buildings in Anambra State of Nigeria",
    authors: "Chinenye A. Chidolue, Chukwura H. Aginam, Uchenna Anyaoha",
    venue: "International Journal of Engineering Research and Development",
    year: "2014",
    abstract: "Analysis of the contributions of the weakness of local aggregates to the failure of buildings in Anambra State of Nigeria, providing insights into the causes of building failures and potential solutions for improving building safety and durability.",
    paperUrl: "https://d1wqtxts1xzle7.cloudfront.net/77506686/J10127378-libre.pdf?1640724745=&response-content-disposition=inline%3B+filename%3DContributions_of_the_Weakness_of_Local_A.pdf&Expires=1742521907&Signature=UE34Yid3l8~vJ1IMUxkn57Qw857Qldlb6M6zQDMOAMJKimP-2o71TYL4Xc4L02KBl2JDdhIp0pTHdBovElKYFDbYhdFN7PYbcXDMedcEv-TyzxYaKSCrB7PNA5qsd43TZlzywVXoFzKaSFmWnxgCtF2XdOMlMry0UaDOzBogZCZeSsQIUWCnZ5HbJDPBZcKFC1Esvo4EBR44fymuW71GGKmfrWkn00B10KEnd3WtSxgJ62EKTvYtSLhNGbqUKXdmpwLgFIMXv~bIqnIcO2UDDVILg-c157wQhqied1rwfmzlvlrVq~8hRfN55mufNo8~YsdrVTswixc5PFVSsUB0Sg__&Key-Pair-Id=APKAJLOHF5GGSLRBV4ZA",
    codeUrl: null,
    type: "journal",
    category: "engineering",
    citation: 29,
    importance: 94
  }
];

// Academic leadership roles
const academicLeadership = [
  {
    id: 1,
    title: "Session Chair - SPIE Smart Structures and NDE for Energy Systems and Industry 4.0",
    role: "Session Chair",
    organization: "SPIE",
    year: "2020",
    session: "Big Data, Data Management, Data Processing, and Data Fusion I",
    description: "Chaired a technical session focused on cutting-edge research in data management and processing for energy systems, with emphasis on machine learning applications for industrial data.",
    url: "https://www.spiedigitallibrary.org/proceedings/Download?urlId=10.1117%2F12.2524987",
    importance: 99
  }
];

// Type icons mapping
const getCategoryIcon = (category: string) => {
  switch(category.toLowerCase()) {
    case 'ai': return <FaRobot className="icon-glow" />;
    case 'energy': return <FaChartLine className="icon-glow" />;
    case 'engineering': return <FaDatabase className="icon-glow" />;
    case 'healthcare': return <FaGlobe className="icon-glow" />;
    case 'ethics': return <FaFileAlt className="icon-glow" />;
    case 'iot': return <FaCode className="icon-glow" />;
    case 'policy': return <FaUsers className="icon-glow" />;
    case 'manufacturing': return <FaCogs className="icon-glow" />;
    default: return <FaBook className="icon-glow" />;
  }
};

const getTypeIcon = (type: string) => {
  switch(type.toLowerCase()) {
    case 'journal': return <FaNewspaper />;
    case 'conference': return <FaBook />;
    case 'preprint': return <FaFileAlt />;
    case 'thesis': return <FaUniversity />;
    default: return <FaFileAlt />;
  }
};

// Match percentage calculation (for Netflix-style match)
const getMatchPercentage = (importance: number) => {
  return Math.floor(importance);
};

const ResearchPublications: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [hoveredItem, setHoveredItem] = useState<number | null>(null);

  // Filter research based on search, type, and category
  const filteredResearch = researchData.filter(item => {
    const matchesSearch = 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
      item.authors.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.abstract.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType ? item.type === selectedType : true;
    const matchesCategory = selectedCategory ? item.category === selectedCategory : true;
    return matchesSearch && matchesType && matchesCategory;
  });

  // Get all unique types and categories
  const types = Array.from(new Set(researchData.map(item => item.type)));
  const categories = Array.from(new Set(researchData.map(item => item.category)));

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: {
        duration: 0.4
      }
    }
  };

  return (
    <div className="research-publications-container">
      <div className="netflix-gradient-overlay">
        <div className="research-publications-header">
          <h1 className="research-publications-title">Research & Publications</h1>
          <p className="research-publications-subtitle">Academic and Industry Contributions</p>
        </div>

        {/* Featured Academic Leadership Section */}
        <div className="academic-leadership">
          <h2 className="leadership-title">
            <FaMedal className="leadership-icon" /> Academic Leadership
          </h2>
          <div className="leadership-cards">
            {academicLeadership.map((item) => (
              <motion.div 
                key={item.id}
                className="leadership-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="netflix-badge">FEATURED</div>
                <div className="leadership-match">
                  <span className="match-percentage">{getMatchPercentage(item.importance)}% Match</span>
                </div>
                <h3 className="leadership-card-title">{item.title}</h3>
                <div className="leadership-role">
                  <FaChalkboardTeacher /> {item.role} • {item.organization} • {item.year}
                </div>
                <div className="leadership-session">
                  <FaUsers /> Session: {item.session}
                </div>
                <p className="leadership-description">{item.description}</p>
                <a 
                  href={item.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="leadership-link"
                >
                  <FaExternalLinkAlt /> View Conference Details
                </a>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Most Cited Research Section */}
        <div className="most-cited-section">
          <h2 className="leadership-title">
            <FaStar className="citation-icon-large" /> Most Cited Research
          </h2>
          {researchData
            .filter(item => item.citation >= 100)
            .map((item) => (
              <motion.div 
                key={item.id}
                className="most-cited-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="citation-badge">{item.citation} Citations</div>
                <h3 className="most-cited-title">{item.title}</h3>
                <div className="most-cited-authors">{item.authors}</div>
                <div className="most-cited-meta">
                  <span className="most-cited-venue">
                    {getTypeIcon(item.type)}
                    {item.venue}, {item.year}
                  </span>
                  <span className="category-badge">
                    {getCategoryIcon(item.category)}
                    {item.category.charAt(0).toUpperCase() + item.category.slice(1)}
                  </span>
                </div>
                <p className="most-cited-abstract">{item.abstract}</p>
                <a 
                  href={item.paperUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="view-paper-link"
                >
                  <FaExternalLinkAlt /> View Publication
                </a>
              </motion.div>
            ))}
        </div>

        <div className="search-and-filter">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search publications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-buttons">
            <button 
              className={`filter-button ${selectedType === null ? 'active' : ''}`}
              onClick={() => setSelectedType(null)}
            >
              All Types
            </button>
            {types.map(type => (
              <button
                key={type}
                className={`filter-button ${selectedType === type ? 'active' : ''}`}
                onClick={() => setSelectedType(type)}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
                {getTypeIcon(type)}
              </button>
            ))}
          </div>
          
          <div className="filter-buttons category-filters">
            <button 
              className={`filter-button ${selectedCategory === null ? 'active' : ''}`}
              onClick={() => setSelectedCategory(null)}
            >
              All Topics
            </button>
            {categories.map(category => (
              <button
                key={category}
                className={`filter-button ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
                {getCategoryIcon(category)}
              </button>
            ))}
          </div>
        </div>

        <motion.div 
          className="research-grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {filteredResearch.map((item, index) => (
            <motion.div 
              key={index}
              className="research-card"
              variants={itemVariants}
              onMouseEnter={() => setHoveredItem(index)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <div className="research-image">
                <div className="category-icon">
                  {getCategoryIcon(item.category)}
                </div>
                {hoveredItem === index && (
                  <div className="hover-overlay">
                    <div className="action-buttons">
                      {item.paperUrl && (
                        <a 
                          href={item.paperUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="action-button"
                        >
                          <FaExternalLinkAlt /> View
                        </a>
                      )}
                      {item.codeUrl && (
                        <a 
                          href={item.codeUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="action-button"
                        >
                          <FaCode /> Code
                        </a>
                      )}
                      <button className="action-button">
                        <FaPlus /> My List
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="research-content">
                <div className="research-match">
                  <span className="match-percentage">{getMatchPercentage(item.importance)}% Match</span>
                  <span className="research-year">{item.year}</span>
                </div>
                
                <h3 className="research-title">{item.title}</h3>
                
                <p className="research-authors">{item.authors}</p>
                
                <div className="research-meta">
                  <span className="research-venue">
                    {getTypeIcon(item.type)}
                    {item.venue}
                  </span>
                  
                  <span className="citation-count">
                    <FaStar className="citation-icon" />
                    {item.citation} Citations
                  </span>
                </div>
                
                <p className="research-abstract">{item.abstract}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
      
      <div className="trending-research">
        <h2>Trending in Energy Research <span className="trending-badge">HOT</span></h2>
        <div className="trending-row">
          {researchData
            .filter(item => item.category === 'energy')
            .sort((a, b) => b.citation - a.citation)
            .slice(0, 4)
            .map((item, index) => (
              <div key={index} className="trending-item">
                <div className="trending-number">{index + 1}</div>
                <div className="trending-content">
                  <h3>{item.title}</h3>
                  <p>{item.venue}, {item.year}</p>
                </div>
              </div>
            ))}
        </div>
      </div>
      
      <div className="netflix-originals research-section">
        <h2>Latest Publications <span className="originals-badge">HIGHLIGHTED</span></h2>
        <div className="suggestion-row">
          {researchData
            .filter(item => parseInt(item.year) >= 2020)
            .sort((a, b) => parseInt(b.year) - parseInt(a.year))
            .slice(0, 4)
            .map((item, index) => (
              <div key={index} className="suggestion-item original-item">
                <div className="netflix-n">N</div>
                <span>{item.title}</span>
                {parseInt(item.year) >= 2021 && <div className="hot-badge">HOT</div>}
              </div>
            ))}
        </div>
      </div>
      
      <div className="research-section">
        <h2>Because you're interested in Manufacturing</h2>
        <div className="suggestion-row">
          {researchData
            .filter(item => item.category === 'manufacturing')
            .slice(0, 4)
            .map((item, index) => (
              <div key={index} className="suggestion-item">
                <div className="suggestion-icon">{getCategoryIcon(item.category)}</div>
                <span>{item.title}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default ResearchPublications; 